import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionsContext';
import {
  callCenterAgentsApi,
  callCenterPopApi,
  show422Error,
  ValidationError,
} from '../utils/api';

const CallCenterContext = createContext(null);

export const useCallCenter = () => {
  const ctx = useContext(CallCenterContext);
  if (!ctx) {
    throw new Error('useCallCenter must be used within CallCenterProvider');
  }
  return ctx;
};

export const normalizeCallPhone = (num) => {
  if (!num) return '';
  const digits = String(num).replace(/\D/g, '');
  if (digits.length >= 7) {
    return digits.slice(-7);
  }
  return digits;
};

export const extractCallIds = (obj) => {
  if (!obj) return [];
  if (typeof obj === 'string' || typeof obj === 'number') {
    const s = String(obj).trim();
    return s ? [s] : [];
  }
  const data = obj.data || obj;
  const call = data.call || {};
  return [
    obj.id,
    data.id,
    data.call_id,
    call.id,
    data.uniqueid,
    data.simotel_uniqueid,
    call.uniqueid,
    call.simotel_uniqueid,
  ]
    .filter(Boolean)
    .map((v) => String(v).trim());
};

export const extractCallPhone = (obj) => {
  if (!obj) return '';
  if (typeof obj === 'string' || typeof obj === 'number') {
    const s = String(obj).trim();
    if (/^\+?\d{7,}$/.test(s)) return normalizeCallPhone(s);
    return '';
  }
  const data = obj.data || obj;
  const call = data.call || {};
  const phone = data.phone || call.phone || '';
  return normalizeCallPhone(phone);
};

export const isSameCall = (itemA, itemB) => {
  if (!itemA || !itemB) return false;

  // 1. Phone numbers check:
  const phoneA = extractCallPhone(itemA);
  const phoneB = extractCallPhone(itemB);

  // If both have phone numbers and they don't match, they CANNOT be the same call!
  if (phoneA && phoneB && phoneA !== phoneB) {
    return false;
  }

  // 2. ID check:
  const idsA = extractCallIds(itemA);
  const idsB = extractCallIds(itemB);

  for (const id of idsA) {
    if (id && idsB.includes(id)) {
      return true;
    }
  }

  // 3. If phone numbers match (and are not empty) and neither had conflicting IDs:
  if (phoneA && phoneB && phoneA === phoneB) {
    return true;
  }

  return false;
};

/** اختیاری — خارج از Provider خطا نمی‌دهد */
export const useCallCenterOptional = () => useContext(CallCenterContext);

export const CallCenterProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { isModuleActive } = usePermissions();

  const isCallCenterActive = isAuthenticated && isModuleActive('callcenter');

  const [myAgentExtension, setMyAgentExtension] = useState(null);
  const [myAgentExtensions, setMyAgentExtensions] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);

  const [screenPops, setScreenPops] = useState([]);
  const [screenPopOpen, setScreenPopOpen] = useState(false);
  const [screenPopData, setScreenPopData] = useState(null);
  const [screenPopLoading, setScreenPopLoading] = useState(false);

  const [callingPhone, setCallingPhone] = useState(null);

  const agentLoadedRef = useRef(false);

  const loadMyAgentExtension = useCallback(async (force = false) => {
    if (!isCallCenterActive || !user?.id) return null;
    if (agentLoadedRef.current && !force) return myAgentExtension;
    setAgentLoading(true);
    try {
      const res = await callCenterAgentsApi.getAll();
      const agents = res?.data ?? [];
      const allMine = agents.filter(
        (a) => Number(a.user_id) === Number(user.id) && a.is_active,
      );
      const exts = allMine.map((a) => a.extension).filter(Boolean);
      const ext = exts[0] ?? null;
      setMyAgentExtension(ext);
      setMyAgentExtensions(exts);
      agentLoadedRef.current = true;
      return ext;
    } catch {
      setMyAgentExtension(null);
      setMyAgentExtensions([]);
      return null;
    } finally {
      setAgentLoading(false);
    }
  }, [isCallCenterActive, user?.id, myAgentExtension]);

  const fetchScreenPop = useCallback(
    async ({ phone, extension, silent = false } = {}) => {
      if (!isCallCenterActive) return null;
      if (!silent) setScreenPopLoading(true);
      try {
        const ext = extension ?? myAgentExtension ?? (await loadMyAgentExtension());
        const res = await callCenterPopApi.getScreenPop({
          phone,
          extension: ext || undefined,
        });
        return res?.data ?? null;
      } catch (error) {
        if (!silent) console.error('Screen pop error:', error);
        return null;
      } finally {
        if (!silent) setScreenPopLoading(false);
      }
    },
    [isCallCenterActive, myAgentExtension, loadMyAgentExtension],
  );

  const screenPopsRef = useRef(screenPops);
  screenPopsRef.current = screenPops;

  const openScreenPop = useCallback(
    async (params = {}) => {
      let data = params.data || {};
      const phone = data?.phone || data?.call?.phone || params.phone;
      const eventName = String(data?.event || params?.event || data?.call?.event || '').toLowerCase().trim();
      const isLifecycleEvent = ['answered', 'answer', 'hangup', 'hangout'].includes(eventName);

      // Check if call already exists in active screen pops
      const existingInRef = screenPopsRef.current.find((p) => isSameCall(p, data || params));

      // اگر تماس قبلاً باز نشده و رویداد زنگ اولیه است و اطلاعات تطبیق CRM در دیتای سوکت نیست، لود می‌کنیم
      if (!existingInRef && !isLifecycleEvent && !data?.match && phone) {
        try {
          const full = await fetchScreenPop({
            phone,
            extension: data?.extension || data?.call?.extension || params.extension,
            silent: true,
          });
          if (full) {
            data = {
              ...(full || {}),
              ...(data || {}),
              call: {
                ...(full.call || {}),
                ...(data.call || {}),
                ...(data.event ? { event: data.event } : {}),
                ...(data.status ? { status: data.status } : {}),
              },
              match: full.match || data?.match,
              recent_calls: full.recent_calls || data?.recent_calls,
            };
          }
        } catch {
          // ignore
        }
      }

      if (!data) return;

      // پاپ‌آپ اسکرین‌پاپ فقط برای تماس‌های ورودی است
      const direction = String(
        data.direction || data.call?.direction || ''
      ).toLowerCase().trim();

      if (direction && direction !== 'incoming' && direction !== 'inbound') {
        return;
      }

      setScreenPops((prev) => {
        const existingIdx = prev.findIndex((p) => isSameCall(p, data));

        if (existingIdx !== -1) {
          const updated = [...prev];
          const existing = updated[existingIdx];
          const newEvent = data.event || data.call?.event || existing.data?.event;
          const newStatus = data.status || data.call?.status || (newEvent ? newEvent : existing.data?.status);

          const mergedData = {
            ...existing.data,
            ...data,
            call: {
              ...(existing.data?.call || {}),
              ...(data.call || {}),
              event: newEvent,
              status: newStatus,
              disposition: data.disposition || data.call?.disposition || existing.data?.call?.disposition,
              ended_at: data.ended_at || data.call?.ended_at || existing.data?.call?.ended_at,
              answered_at: data.answered_at || data.call?.answered_at || existing.data?.call?.answered_at,
              ...(data.duration != null ? { duration: data.duration } : {}),
              ...(data.billsec != null ? { billsec: data.billsec } : {}),
            },
            match: data.match || existing.data?.match,
            recent_calls: data.recent_calls || existing.data?.recent_calls,
            event: newEvent,
            status: newStatus,
            disposition: data.disposition || data.call?.disposition || existing.data?.disposition,
            ended_at: data.ended_at || data.call?.ended_at || existing.data?.ended_at,
            answered_at: data.answered_at || data.call?.answered_at || existing.data?.answered_at,
            duration: data.duration != null ? data.duration : existing.data?.duration,
            billsec: data.billsec != null ? data.billsec : existing.data?.billsec,
          };

          updated[existingIdx] = {
            ...existing,
            data: mergedData,
          };
          return updated;
        }

        const primaryId = String(
          data.call_id || data.call?.id || data.uniqueid || data.simotel_uniqueid || data.phone || Date.now()
        ).trim();

        return [
          {
            id: primaryId,
            data,
            isExpanded: false,
            createdAt: Date.now(),
          },
          ...prev,
        ];
      });

      setScreenPopData((prev) => {
        if (!prev) return data;
        if (isSameCall({ id: prev.call_id || prev.call?.id || prev.phone, data: prev }, data)) {
          const newEvent = data.event || data.call?.event || prev.event;
          const newStatus = data.status || data.call?.status || (newEvent ? newEvent : prev.status);
          return {
            ...prev,
            ...data,
            call: {
              ...(prev.call || {}),
              ...(data.call || {}),
              event: newEvent,
              status: newStatus,
              disposition: data.disposition || data.call?.disposition || prev.call?.disposition,
              ended_at: data.ended_at || data.call?.ended_at || prev.call?.ended_at,
              answered_at: data.answered_at || data.call?.answered_at || prev.call?.answered_at,
              ...(data.duration != null ? { duration: data.duration } : {}),
              ...(data.billsec != null ? { billsec: data.billsec } : {}),
            },
            match: data.match || prev.match,
            recent_calls: data.recent_calls || prev.recent_calls,
            event: newEvent,
            status: newStatus,
            disposition: data.disposition || data.call?.disposition || prev.disposition,
            ended_at: data.ended_at || data.call?.ended_at || prev.ended_at,
            answered_at: data.answered_at || data.call?.answered_at || prev.answered_at,
            duration: data.duration != null ? data.duration : prev.duration,
            billsec: data.billsec != null ? data.billsec : prev.billsec,
          };
        }
        return data;
      });
      setScreenPopOpen(true);
    },
    [fetchScreenPop],
  );

  const updateScreenPop = useCallback((updater, targetCallId) => {
    setScreenPops((prev) => {
      if (prev.length === 0) return prev;
      return prev.map((item) => {
        if (targetCallId && !isSameCall(item, targetCallId)) {
          return item;
        }
        const updatedData =
          typeof updater === 'function' ? updater(item.data) : { ...item.data, ...updater };
        return {
          ...item,
          data: updatedData,
        };
      });
    });

    setScreenPopData((prev) => {
      if (!prev) return prev;
      if (targetCallId && !isSameCall({ id: prev.call_id || prev.call?.id || prev.phone, data: prev }, targetCallId)) {
        return prev;
      }
      return typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
    });
  }, []);

  const closeScreenPop = useCallback((id) => {
    setScreenPops((prev) => {
      if (!id) {
        setScreenPopOpen(false);
        setScreenPopData(null);
        return [];
      }
      const remaining = prev.filter((p) => !isSameCall(p, id));
      if (remaining.length === 0) {
        setScreenPopOpen(false);
        setScreenPopData(null);
      } else {
        setScreenPopData(remaining[0].data);
      }
      return remaining;
    });
  }, []);

  const expandScreenPop = useCallback((id) => {
    setScreenPops((prev) =>
      prev.map((p) => ({
        ...p,
        isExpanded: isSameCall(p, id),
      }))
    );
  }, []);

  const collapseScreenPop = useCallback((id) => {
    setScreenPops((prev) =>
      prev.map((p) => (isSameCall(p, id) ? { ...p, isExpanded: false } : p))
    );
  }, []);

  const toggleScreenPopExpand = useCallback((id) => {
    setScreenPops((prev) =>
      prev.map((p) => (isSameCall(p, id) ? { ...p, isExpanded: !p.isExpanded } : p))
    );
  }, []);

  const clickToCall = useCallback(
    async (phone, { extension } = {}) => {
      if (!phone?.trim()) {
        message.warning('شماره تماس موجود نیست');
        return false;
      }
      if (!isCallCenterActive) {
        message.warning('ماژول مرکز تماس فعال نیست');
        return false;
      }

      const normalized = phone.trim();
      setCallingPhone(normalized);
      try {
        const ext = extension ?? myAgentExtension ?? (await loadMyAgentExtension());
        const res = await callCenterPopApi.clickToCall({
          destination: normalized,
          phone: normalized,
          extension: ext || undefined,
        });
        message.success(res?.message || 'درخواست برقراری تماس ارسال شد');
        return true;
      } catch (error) {
        console.error('Click-to-call error:', error);
        if (error instanceof ValidationError) {
          show422Error(error);
        } else {
          message.error(error?.message || 'خطا در برقراری تماس');
        }
        return false;
      } finally {
        setCallingPhone(null);
      }
    },
    [isCallCenterActive, myAgentExtension, loadMyAgentExtension],
  );

  const value = useMemo(
    () => ({
      isCallCenterActive,
      myAgentExtension,
      myAgentExtensions,
      agentLoading,
      loadMyAgentExtension,
      screenPops,
      screenPopOpen: screenPops.length > 0 || screenPopOpen,
      screenPopData: screenPops[0]?.data || screenPopData,
      screenPopLoading,
      openScreenPop,
      updateScreenPop,
      closeScreenPop,
      expandScreenPop,
      collapseScreenPop,
      toggleScreenPopExpand,
      fetchScreenPop,
      clickToCall,
      callingPhone,
      isCalling: (phone) => callingPhone != null && callingPhone === phone?.trim(),
    }),
    [
      isCallCenterActive,
      myAgentExtension,
      myAgentExtensions,
      agentLoading,
      loadMyAgentExtension,
      screenPops,
      screenPopOpen,
      screenPopData,
      screenPopLoading,
      openScreenPop,
      updateScreenPop,
      closeScreenPop,
      expandScreenPop,
      collapseScreenPop,
      toggleScreenPopExpand,
      fetchScreenPop,
      clickToCall,
      callingPhone,
    ],
  );

  return (
    <CallCenterContext.Provider value={value}>
      {children}
    </CallCenterContext.Provider>
  );
};
